"use client"
import { useRouter, usePathname } from 'next/navigation';
import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { MenuIcon, X } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import ComingSoonButton from '@/components/ui/coming-soon-button';

// Throttle function
const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
};

// Memoized Logo Component
const Logo = memo(({ isHomePage, scrollY, logoTextClasses }) => (
  <Link href="/" className="flex items-center gap-3 group">
    <motion.div 
      className="relative w-10 h-10 rounded-full overflow-hidden shadow-md"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Image
        src="/logo/logo orange background embossed.jpg"
        alt="Mochi Mochi Chillin"
        fill
        sizes="40px"
        className="object-cover transform transition-transform duration-300 group-hover:scale-110"
      />
    </motion.div>
    <motion.span 
      className={logoTextClasses}
      whileHover={{ scale: 1.02 }}
    >
      Mochi Mochi Chillin
    </motion.span>
  </Link>
));
Logo.displayName = 'Logo';

// Memoized Navigation Item Component
const NavItem = memo(({ item, isActive, navItemClasses }) => (
  <Link href={item.path}>
    <motion.span
      className={navItemClasses(isActive(item.path))}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {item.name}
    </motion.span>
  </Link>
));
NavItem.displayName = 'NavItem';

// Memoized Order Button Component
const OrderButton = memo(({ isHomePage, scrollY }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <ComingSoonButton 
      className={`
        relative overflow-hidden font-heading text-[15px] px-6 py-2.5 rounded-full
        transition-all duration-300 shadow-sm hover:shadow-md
        ${isHomePage && scrollY <= 50
          ? 'bg-primary-orange/10 text-primary-orange hover:bg-primary-orange/20 border border-primary-orange/20'
          : 'bg-primary-orange text-white hover:bg-primary-orange/90'
        }
      `}
    >
      Order Now
    </ComingSoonButton>
  </motion.div>
));
OrderButton.displayName = 'OrderButton';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const router = useRouter();
  const pathname = usePathname();

  // Memoize navigation items
  const navItems = useMemo(() => [
    { name: 'Home', path: '/' },
    { name: 'Menu', path: '/menu' },
    { name: 'Our Story', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ], []);

  // Memoize active check function
  const isActive = useCallback((path) => pathname === path, [pathname]);

  const isHomePage = pathname === '/';

  // Memoize class generation functions
  const headerClasses = useMemo(() => `fixed w-full z-50 transition-all duration-500 ${
    !isVisible ? '-translate-y-full' : 'translate-y-0'
  } ${
    isHomePage 
      ? scrollY > 50 
        ? 'bg-white/95 backdrop-blur-md shadow-lg py-3' 
        : 'bg-transparent py-5'
      : 'bg-white/95 backdrop-blur-md shadow-lg py-3'
  }`, [isVisible, isHomePage, scrollY]);

  const navItemClasses = useCallback((active) => `
    relative px-4 py-2 font-heading text-[15px] font-medium transition-all duration-300
    ${isHomePage
      ? scrollY > 50
        ? active ? 'text-primary-orange' : 'text-neutral-600 hover:text-neutral-800'
        : active ? 'text-white' : 'text-white/90 hover:text-white'
      : active ? 'text-primary-orange' : 'text-neutral-600 hover:text-neutral-800'
    }
  `, [isHomePage, scrollY]);

  const logoTextClasses = useMemo(() => `
    font-heading text-xl font-bold tracking-tight transition-all duration-300
    ${isHomePage 
      ? scrollY > 50 
        ? 'bg-gradient-to-r from-primary-orange to-primary-light bg-clip-text text-transparent' 
        : 'text-white'
      : 'bg-gradient-to-r from-primary-orange to-primary-light bg-clip-text text-transparent'
    }
  `, [isHomePage, scrollY]);

  // Throttled scroll handler
  useEffect(() => {
    const handleScroll = throttle(() => {
      const currentScrollY = window.scrollY;
      setIsVisible(currentScrollY < lastScrollY || currentScrollY < 50);
      setLastScrollY(currentScrollY);
      setScrollY(currentScrollY);
    }, 100); // Throttle to 100ms

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  return (
    <header className={headerClasses}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <nav className="flex items-center justify-between">
          <Logo isHomePage={isHomePage} scrollY={scrollY} logoTextClasses={logoTextClasses} />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <div className="flex items-center gap-1">
              {navItems.map((item) => (
                <NavItem 
                  key={item.name} 
                  item={item} 
                  isActive={isActive} 
                  navItemClasses={navItemClasses} 
                />
              ))}
            </div>
            <OrderButton isHomePage={isHomePage} scrollY={scrollY} />
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className={`md:hidden p-2.5 rounded-full transition-colors duration-300 ${
              isHomePage 
                ? scrollY > 50 
                  ? 'text-neutral-600 hover:bg-neutral-100' 
                  : 'text-white hover:bg-white/10'
                : 'text-neutral-600 hover:bg-neutral-100'
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            whileTap={{ scale: 0.95 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={isMenuOpen ? "close" : "menu"}
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0 }}
                exit={{ opacity: 0, rotate: 90 }}
                transition={{ duration: 0.2 }}
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <MenuIcon className="h-6 w-6" />
                )}
              </motion.div>
            </AnimatePresence>
          </motion.button>
        </nav>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className={`md:hidden overflow-hidden ${
                isHomePage && scrollY <= 50 
                  ? 'bg-neutral-900/95 backdrop-blur-md mt-3 rounded-2xl'
                  : 'bg-white/95 backdrop-blur-md mt-3 rounded-2xl border border-neutral-100'
              }`}
            >
              <motion.div 
                className="py-6 space-y-2"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 + 0.2 }}
                  >
                    <Link href={item.path}>
                      <motion.span
                        whileHover={{ x: 4 }}
                        whileTap={{ scale: 0.98 }}
                        className={`
                          block font-heading text-base font-medium px-4 py-3 rounded-xl
                          ${isActive(item.path)
                            ? isHomePage && scrollY <= 50
                              ? 'bg-white/10 text-white'
                              : 'bg-primary-orange/10 text-primary-orange'
                            : isHomePage && scrollY <= 50
                              ? 'text-white/90 hover:text-white hover:bg-white/5'
                              : 'text-neutral-600 hover:text-neutral-800 hover:bg-neutral-50'
                          }
                        `}
                      >
                        {item.name}
                      </motion.span>
                    </Link>
                  </motion.div>
                ))}
                <motion.div 
                  className="pt-2 px-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <ComingSoonButton 
                    className={`
                      w-full font-heading text-base py-3 rounded-xl 
                      ${isHomePage && scrollY <= 50
                        ? 'bg-white text-neutral-900 hover:bg-white/90'
                        : 'bg-primary-orange text-white hover:bg-primary-orange/90'
                      } 
                      transition-colors duration-300
                    `}
                  >
                    Order Now
                  </ComingSoonButton>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default memo(Header);